import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
import React, { useState, useEffect, useCallback } from 'react';
import { Edit, Trash2, Plus, History, ShieldPlus } from 'lucide-react';
import { fetchTiposPago, TipoPago } from '../../../services/tiposPagoService';
import AddTiposPagos from '../../../components/TiposPagos/AddTiposPagos';
import EditTiposPagos from '../../../components/TiposPagos/EditTiposPagos';
import DeleteTiposPagos from '../../../components/TiposPagos/DeleteTiposPagos';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';
import { useNavigate } from 'react-router-dom';
import Loading from '../../../components/Loading';

const TiposPagosPage: React.FC = () => {
    const [tiposPago, setTiposPago] = useState<TipoPago[]>([]);
    const [loading, setLoading] = useState(true);

    const [modalAbierto, setModalAbierto] = useState(false);
    const [modalEditarAbierto, setModalEditarAbierto] = useState(false);
    const [modalEliminarAbierto, setModalEliminarAbierto] = useState(false);
    const [tipoPagoEditando, setTipoPagoEditando] = useState<TipoPago | null>(null);
    const [tipoPagoEliminando, setTipoPagoEliminando] = useState<TipoPago | null>(null);

    // Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Sesión expirada
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const navigate = useNavigate();

    const userLevelId = user?.user_level_id ?? 0;
    // Ajusta el nombre del modelo según tu backend (p.ej. 'tipopago', 'tipo_pago')
    const perm = useModelPermission('tipopago', userLevelId);

    const cargarTiposPago = useCallback(async () => {
        setLoading(true);
        try {
            if (perm?.can_read) {
                const data = await fetchTiposPago();
                const dataOrdenada = [...data].sort((a, b) => a.id - b.id);
                setTiposPago(dataOrdenada);
            } else {
                setTiposPago([]);
            }
        } catch (err) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm]);

    useEffect(() => {
        if (perm === null) return;
        cargarTiposPago();
    }, [perm, cargarTiposPago]);

    const abrirModalEdicion = (tipo: TipoPago) => {
        setTipoPagoEditando(tipo);
        setModalEditarAbierto(true);
    };

    const abrirModalEliminacion = (tipo: TipoPago) => {
        setTipoPagoEliminando(tipo);
        setModalEliminarAbierto(true);
    };

    // Cálculo de qué mostrar en la página actual
    const totalPages = Math.ceil(tiposPago.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedTiposPago = tiposPago.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null || (loading && tiposPago.length === 0)) {
        return <Loading text="Cargando tipos de pago..." />;
    }

    return <><LumaResourceWorkspace title="Tipos de Pago" description="Medios de pago autorizados" group="Pagos" rows={tiposPago} columns={[{key:'nombre',label:'Nombre'},{key:'configuracion_detail',label:'Configuración',render:r=>r.configuracion_detail?.nombre||'Sin configuración'},{key:'activo',label:'Estado'}]} idKey="id" nameKey="nombre" statusKey="activo" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setModalAbierto(true)}:undefined} onEdit={perm?.can_update?r=>{setTipoPagoEditando(r);setModalEditarAbierto(true);}:undefined} onDelete={perm?.can_delete?r=>{setTipoPagoEliminando(r);setModalEliminarAbierto(true);}:undefined} history=""/><ExpiredSessionModal
                isOpen={isSessionExpired}
                onClose={async () => {
                    setIsSessionExpired(false);
                    await logout();
                }}
            /><AddTiposPagos
                isOpen={modalAbierto}
                onClose={() => setModalAbierto(false)}
                onTipoPagoCreated={cargarTiposPago}
            /><EditTiposPagos
                isOpen={modalEditarAbierto}
                onClose={() => setModalEditarAbierto(false)}
                onTipoPagoUpdated={cargarTiposPago}
                tipoPago={tipoPagoEditando}
            /><DeleteTiposPagos
                isOpen={modalEliminarAbierto}
                onClose={() => setModalEliminarAbierto(false)}
                onTipoPagoDeleted={cargarTiposPago}
                tipoPago={tipoPagoEliminando}
            /></>;
};

export default TiposPagosPage;
